import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import { buildInitialObsState } from "features/pregnancy/utils/buildInitialObsState";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { useFoetalObservations } from "./useFoetalObservations";
import { useMncmsOutboundMessages } from "./useMncmsOutboundMessages";
import { useSaveAnteNatalConsultation } from "./useSaveAnteNatalConsultation";
import { useSaveFoetalObservations } from "./useSaveFoetalObservations";

// Persisted outbound lifecycle status that means the send did NOT land — a
// FAILED row doesn't count as "sent", so the visit can be sent fresh again
// (matches the BE 409 guard, which ignores FAILED rows).
const MNCMS_FAILED_STATUS = "FAILED";

export const useAnteNatalConsultationSave = ({
  tenantVal,
  patientId,
  pregnancyId,
  anteNatalConsultation,
  foetuses,
  sendMncmsMessage,
  form,
  closeView,
}) => {
  const [savedConsultationId, setSavedConsultationId] = useState(
    anteNatalConsultation?.id ?? null
  );
  // True only once we've sent within this session (flips the button locally
  // before any refetch settles).
  const [sentThisSession, setSentThisSession] = useState(false);
  const [sendError, setSendError] = useState(null);
  // Which button is in flight ("save" | "send") so each shows its own loading.
  const [pendingAction, setPendingAction] = useState(null);
  const finishMode = useRef(null);

  const consultationId = savedConsultationId ?? anteNatalConsultation?.id ?? null;

  // Has a message already been sent for this visit? The outbound message log is
  // the source of truth — it's the same table the BE 409-gates on, so the button
  // can never disagree with the guard. A visit is "already sent" when it has at
  // least one non-FAILED outbound row (the guard's `existsActiveForConsultation`
  // rule); a FAILED-only visit is a legitimate fresh send, not a correction.
  // Once the log has loaded it is the ONLY truth: an empty result means "not
  // sent", even if the consultation's own `sentToMncms` / `mncmsMessageId` flag
  // says otherwise — that flag is a denormalised copy that can drift true with
  // no real outbound message behind it, which would route us to the correction
  // endpoint and 409 ("no prior message to correct"). We consult the flag only
  // before the query resolves, as a best-effort hint.
  const { data: outboundMessages } = useMncmsOutboundMessages(pregnancyId);
  const isOutboundLogLoaded = Array.isArray(outboundMessages);
  const consultationOutboundRows = (outboundMessages ?? []).filter(
    (message) => message.anteNatalConsultationId === consultationId
  );
  const wasSentPerLog = isOutboundLogLoaded
    ? consultationOutboundRows.some(
        (message) => message.status !== MNCMS_FAILED_STATUS
      )
    : !!(anteNatalConsultation?.sentToMncms || anteNatalConsultation?.mncmsMessageId);
  const wasSent = sentThisSession || wasSentPerLog;

  const { data: existingObservations } = useFoetalObservations(consultationId);

  // Foetal observations live inside react-hook-form (keyed by foetusIdentifier)
  // so zod validates them natively. The form is seeded with empty entries at
  // mount; once the saved observations load, replace them in place.
  useEffect(() => {
    if (existingObservations && foetuses.length > 0) {
      form.setValue(
        "foetalObservations",
        buildInitialObsState(foetuses, existingObservations),
        { shouldDirty: false, shouldValidate: false }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingObservations]);

  const { saveAll: saveFoetalObservations } = useSaveFoetalObservations({
    consultationId,
    pregnancyId,
    foetuses,
    foetalObsValues: form.watch("foetalObservations") ?? {},
    existingObservations,
  });

  const { mutate: saveConsultation, isPending } = useSaveAnteNatalConsultation({
    tenantVal,
    patientId,
    pregnancyId,
    consultationId,
    consultationDate: anteNatalConsultation?.consultationDate,
    onSuccess: (data) => {
      const savedId = data?.id ?? null;

      if (savedId) setSavedConsultationId(savedId);

      const resolvedId = savedId ?? savedConsultationId ?? anteNatalConsultation?.id;

      if (resolvedId) saveFoetalObservations(resolvedId);

      const mode = finishMode.current;
      finishMode.current = null;

      if (mode === "send") {
        const idToSend = savedId ?? savedConsultationId ?? anteNatalConsultation?.id;
        setSendError(null);
        // Success / error toasts are handled inside useSendMncmsMessage
        // (it surfaces the BE error message verbatim). A correction is used
        // only when this visit already has a sent message.
        sendMncmsMessage(
          { consultationId: idToSend, isCorrection: wasSent },
          {
            onSuccess: () => {
              setSentThisSession(true);
              setPendingAction(null);
              form.reset();
              closeView();
            },
            onError: () => {
              setSendError("Send failed. Please try again.");
              setPendingAction(null);
            },
          }
        );
      } else if (mode === "close") {
        toast.success("Ante-natal consultation saved.");
        setPendingAction(null);
        form.reset();
        closeView();
      } else {
        toast.success("Ante-natal consultation saved.");
        setPendingAction(null);
      }
    },
  });

  // Clear the "save" loading state once the save mutation settles
  // (covers the error path; the "send" flow clears itself in its callbacks).
  useEffect(() => {
    if (!isPending && pendingAction === "save") setPendingAction(null);
  }, [isPending, pendingAction]);

  const handleSave = form.handleSubmit((values) => {
    finishMode.current = null;
    setPendingAction("save");
    saveConsultation(values);
  }, onInvalidSubmit("useAnteNatalConsultationSave"));

  const handleFinish = form.handleSubmit((values) => {
    finishMode.current = "close";
    setPendingAction("save");
    saveConsultation(values);
  }, onInvalidSubmit("useAnteNatalConsultationSave"));

  const handleFinishAndSend = form.handleSubmit((values) => {
    finishMode.current = "send";
    setPendingAction("send");
    saveConsultation(values);
  }, onInvalidSubmit("useAnteNatalConsultationSave"));

  // Retry / correction: the consultation is already saved, so just re-send
  // the MN-CMS message instead of re-saving the whole consultation.
  const handleRetrySend = () => {
    const idToSend = savedConsultationId ?? anteNatalConsultation?.id;
    if (!idToSend) return;

    setPendingAction("send");
    setSendError(null);
    sendMncmsMessage(
      { consultationId: idToSend, isCorrection: wasSent },
      {
        onSuccess: () => {
          setSentThisSession(true);
          form.reset();
          closeView();
        },
        onError: () => {
          setSendError("Send failed. Please try again.");
        },
      }
    );
  };

  return {
    consultationId,
    wasSent,
    sendError,
    isPending,
    pendingAction,
    handleSave,
    handleFinish,
    handleFinishAndSend,
    handleRetrySend,
  };
};
